import type { TCustomDomain } from './api';

export type TDomainPhase = 'dns' | 'certificate' | 'answering' | 'live' | 'lost';

export const domainPhase = (domain: TCustomDomain): TDomainPhase => {
  if (domain.state === 'lost') return 'lost';
  if (domain.state === 'pending') return 'dns';
  if (!domain.tlsOkAt) return 'certificate';
  return domain.answeringAt ? 'live' : 'answering';
};

export const PHASE_DOT: Record<TDomainPhase, string> = {
  dns: 'bg-faint',
  certificate: 'bg-motion',
  answering: 'bg-motion',
  live: 'bg-success',
  lost: 'bg-danger',
};

export type TStepMark = 'done' | 'current' | 'todo';

export const STEPS = [
  'Hostname added',
  'Record found at your DNS',
  'Certificate issued',
  'Answering requests',
] as const;

export const stepMarks = (domain: TCustomDomain): TStepMark[] => {
  const reached = {
    dns: 1,
    lost: 1,
    certificate: 2,
    answering: 3,
    live: 4,
  }[domainPhase(domain)];
  return STEPS.map((_, index) =>
    index < reached ? 'done' : index === reached ? 'current' : 'todo',
  );
};

export const zoneOf = (hostname: string): string => hostname.split('.').slice(-2).join('.');

export interface IDnsInstruction {
  type: 'CNAME' | 'A';
  name: string;
  values: string[];
}

// A root domain cannot hold a CNAME; it gets our addresses as A records instead.
export const recordToCreate = (
  domain: TCustomDomain,
  cnameTarget: string,
  targetAddresses: string[],
): IDnsInstruction => {
  const labels = domain.hostname.split('.');
  if (domain.apex) return { type: 'A', name: '@', values: targetAddresses };
  return { type: 'CNAME', name: labels.slice(0, -2).join('.'), values: [cnameTarget] };
};

// Traefik renews thirty days out; still under twenty means renewal is failing.
export const renewalLate = (domain: TCustomDomain, now: number): boolean =>
  domain.tlsExpiresAt !== null && Date.parse(domain.tlsExpiresAt) - now < 20 * 86_400_000;
